import {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  APIFieldGlobalAttributes,
  FieldGlobalAttributes,
} from '@ardoq/api-types';
import { createStubImplementation } from '@ardoq/stub-implementation';
import type { TextAreaFormat } from '@ardoq/data-model';

type GetByNameOptions = {
  includeTemplateFields?: boolean;
};

export type GetGlobalFieldByName = (
  fieldName: string
) => FieldGlobalAttributes | null;

export type GetAllFieldsOfWorkspace = (
  workspaceId: ArdoqId
) => APIFieldAttributes[];

export type GetFieldsOfWorkspace = (
  workspaceId: ArdoqId,
  fieldTypes: APIFieldType[]
) => APIFieldAttributes[];

export type GetAllowedValues = (fieldId: ArdoqId) => string[];

type FormatArgs = {
  fieldId: ArdoqId;
  value: unknown;
  fieldType?: APIFieldType;
  excludeTime?: boolean;
  textAreaFormat?: TextAreaFormat;
};
export type Format = (args: FormatArgs) => string | null;

export type GetLabel = (fieldId: ArdoqId) => string | null;

export type AppliesToComponent = (
  fieldId: ArdoqId,
  componentId: ArdoqId
) => boolean;

export type GetByName = (
  fieldName: string,
  options?: GetByNameOptions
) => APIFieldAttributes | null;

export type GetNumberFormatByName = (fieldName: string) => string | null;

export type IsExternallyManagedForComponentsWithinWorkspace = (
  fieldName: string,
  workspaceId: ArdoqId
) => boolean;

export type IsExternallyManagedForReferencesWithinWorkspace = (
  fieldName: string,
  workspaceId: ArdoqId
) => boolean;

export type GetFieldDataForFiltersAndFormatting = (workspaceIds: ArdoqId[]) => {
  relatedFieldsByName: Record<string, APIFieldGlobalAttributes>;
  componentFields: APIFieldGlobalAttributes[];
  referenceFields: APIFieldGlobalAttributes[];
};

type PickGlobalFieldAttributes = ({
  calculatedFieldSettings,
  defaultValue,
  description,
  label,
  name,
  type,
  numberFormatOptions,
  model,
}: APIFieldAttributes) => APIFieldGlobalAttributes;
export type GetFieldAttributes = (id: ArdoqId) => APIFieldAttributes | null;
export type ExistsByName = (fieldName: string) => boolean;
export type GetFieldsCount = () => number;

export type GetFieldsByComponentType = (
  componentTypeId: ArdoqId,
  modelId: ArdoqId
) => APIFieldAttributes[];
export type GetFieldsByReferenceType = (
  referenceTypeId: number,
  modelId: ArdoqId
) => APIFieldAttributes[];
export type GetAllFields = () => APIFieldAttributes[];

type GetFieldValueForComponentOrReference = (
  modelId: ArdoqId,
  fieldName: string
) => any;

type FormatFieldValueForComponentOrReference = (
  fieldId: ArdoqId,
  modelId: ArdoqId
) => string | null;

export type GetFieldTypeFromFieldName = (
  fieldName: string
) => APIFieldType | null;

export type FieldInterfaceImplementation = {
  getAllFieldsOfWorkspace: GetAllFieldsOfWorkspace;
  getFieldsOfWorkspace: GetFieldsOfWorkspace;
  getAllowedValues: GetAllowedValues;
  format: Format;
  getLabel: GetLabel;
  appliesToComponent: AppliesToComponent;
  getByName: GetByName;
  getGlobalFieldByName: GetGlobalFieldByName;
  getNumberFormatByName: GetNumberFormatByName;
  isExternallyManagedForComponentsWithinWorkspace: IsExternallyManagedForComponentsWithinWorkspace;
  isExternallyManagedForReferencesWithinWorkspace: IsExternallyManagedForReferencesWithinWorkspace;
  getAllFields: GetAllFields;
  getFieldsByComponentType: GetFieldsByComponentType;
  getFieldsByReferenceType: GetFieldsByReferenceType;
  getFieldDataForFiltersAndFormatting: GetFieldDataForFiltersAndFormatting;
  pickGlobalFieldAttributes: PickGlobalFieldAttributes;
  get: GetFieldAttributes;
  existsByName: ExistsByName;
  getFieldsCount: GetFieldsCount;
  formatFieldValueForComponentOrReference: FormatFieldValueForComponentOrReference;
  getFieldValueForComponentOrReference: GetFieldValueForComponentOrReference;
  getFieldTypeFromFieldName: GetFieldTypeFromFieldName;
};

const defaultImplementation: FieldInterfaceImplementation = {
  getAllFieldsOfWorkspace: () => [],
  getFieldsOfWorkspace: () => [],
  getAllowedValues: () => [],
  format: () => null,
  getLabel: () => null,
  appliesToComponent: () => false,
  getByName: () => null,
  getGlobalFieldByName: () => null,
  getNumberFormatByName: () => null,
  isExternallyManagedForComponentsWithinWorkspace: () => false,
  isExternallyManagedForReferencesWithinWorkspace: () => false,
  getAllFields: () => [],
  getFieldsByComponentType: () => [],
  getFieldsByReferenceType: () => [],
  getFieldDataForFiltersAndFormatting: () => ({
    relatedFieldsByName: {},
    componentFields: [],
    referenceFields: [],
  }),
  pickGlobalFieldAttributes: () => ({
    calculatedFieldSettings: undefined,
    defaultValue: null,
    description: '',
    label: '',
    name: '',
    type: APIFieldType.TEXT,
    numberFormatOptions: undefined,
    model: '',
  }),
  get: () => null,
  existsByName: () => false,
  getFieldsCount: () => 0,
  formatFieldValueForComponentOrReference: () => null,
  getFieldValueForComponentOrReference: () => null,
  getFieldTypeFromFieldName: () => null,
};

const {
  getAllFieldsOfWorkspace,
  getFieldsOfWorkspace,
  getAllowedValues,
  format,
  getLabel,
  appliesToComponent,
  getByName,
  getGlobalFieldByName,
  getNumberFormatByName,
  isExternallyManagedForComponentsWithinWorkspace,
  isExternallyManagedForReferencesWithinWorkspace,
  getAllFields,
  getFieldsByComponentType,
  getFieldsByReferenceType,
  getFieldDataForFiltersAndFormatting,
  pickGlobalFieldAttributes,
  get,
  existsByName,
  getFieldsCount,
  formatFieldValueForComponentOrReference,
  getFieldValueForComponentOrReference,
  getFieldTypeFromFieldName,
  registerImplementation,
} = createStubImplementation('@ardoq/field-interface', defaultImplementation);

/**
 * `fieldInterface` is a Backbone-era singleton: every member reads the field
 * collection -- and for formatting, the current locale and org users -- from
 * global mutable state at call time.
 *
 * The app is stream-based now. Resolve fields from `fields$`
 * (`streams/fields/fields$.ts`) or `globalFields$` at a permitted boundary -- a
 * `*$.ts` file, a file inside a `*$/` folder, or a `*routines.ts` -- and thread
 * the resolved data downstream as plain parameters.
 *
 * The pure counterparts are `fieldOperations` (`@ardoq/core`) for the type
 * predicates and value reads -- `getFieldValue`, `isDateOnlyField`,
 * `isDateTimeField`, `isNumberField`, `isListField`, `isURLField`,
 * `getListFieldOptions`, `appliesToComponentType`, `appliesToReferenceType` --
 * and `fieldUtils` (`@ardoq/scope-data`) for label and value formatting
 * (`fieldAttributesToLabel`, `formatFieldValueForComponentOrReference`,
 * `getFieldValueForComponentOrReference`, `getAllFieldsInWorkspace`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const fieldInterface = {
  registerImplementation,
  defaultImplementation,
  /**
   * @deprecated Call `fieldUtils.getAllFieldsInWorkspace` (`@ardoq/scope-data`), or read `fieldsByWorkspaceId$` (`streams/fields/fieldsByWorkspaceId$.ts`).
   */
  getAllFieldsOfWorkspace,
  /**
   * @deprecated Call `fieldUtils.getAllFieldsInWorkspace` (`@ardoq/scope-data`), or read `fieldsByWorkspaceId$` (`streams/fields/fieldsByWorkspaceId$.ts`).
   */
  getFieldsOfWorkspace,
  /**
   * @deprecated Call `fieldOperations.getListFieldOptions` (`@ardoq/core`) with the field resolved from `fields$`.
   */
  getAllowedValues,
  /**
   * @deprecated Call the pure `fieldUtils.fieldAttributesToLabel` (`@ardoq/scope-data`) with a field from `fields$`; this member snapshots `i18nLocale$` and `orgUsers$` internally.
   */
  format,
  /**
   * @deprecated Read `fields$.byId[id]?.label`, or call `fieldUtils.getLabel` / `fieldUtils.getLabelByFieldName` (`@ardoq/scope-data`).
   */
  getLabel,
  /**
   * @deprecated Call `fieldOperations.appliesToComponentType` (`@ardoq/core`) or `fieldUtils.appliesToComponent` (`@ardoq/scope-data`).
   */
  appliesToComponent,
  /**
   * @deprecated Read `fields$.byName`.
   */
  getByName,
  /**
   * @deprecated Read `globalFields$.byName` (`streams/fields/globalFields$.ts`).
   */
  getGlobalFieldByName,
  /**
   * @deprecated Read `fields$.byName` and narrow with `fieldOperations.isNumberField` (`@ardoq/core`).
   */
  getNumberFormatByName,
  /**
   * @deprecated Call `componentUtils.isFieldExternallyManaged(scopeData, componentId, fieldName)` (`@ardoq/scope-data`), or check the workspace integrations from `workspaceUtils.getWorkspaceIntegrations`.
   */
  isExternallyManagedForComponentsWithinWorkspace,
  /**
   * @deprecated Check the workspace integrations from `workspaceUtils.getWorkspaceIntegrations` (`@ardoq/scope-data`) against the reference field names.
   */
  isExternallyManagedForReferencesWithinWorkspace,
  /**
   * @deprecated Read `fields$.models`.
   */
  getAllFields,
  /**
   * @deprecated Read `fields$.byComponentType`, or call `fieldUtils.getFieldsByComponentType` (`@ardoq/scope-data`).
   */
  getFieldsByComponentType,
  /**
   * @deprecated Read `fields$.byReferenceType`, or call `fieldUtils.getFieldsByReferenceType` (`@ardoq/scope-data`).
   */
  getFieldsByReferenceType,
  /**
   * @deprecated Call `fieldUtils.getFieldData` (`@ardoq/scope-data`) with fields from `fields$`.
   */
  getFieldDataForFiltersAndFormatting,
  /**
   * @deprecated Join `globalFields$` and pick from the resolved field with `fieldOperations` (`@ardoq/core`).
   */
  pickGlobalFieldAttributes,
  /**
   * @deprecated Read `fields$.byId[id]`.
   */
  get,
  /**
   * @deprecated Read `fields$.byName`.
   */
  existsByName,
  /**
   * @deprecated Read `fields$.models.length`.
   */
  getFieldsCount,
  /**
   * @deprecated Call `fieldUtils.formatFieldValueForComponentOrReference` (`@ardoq/scope-data`) with fields from `fields$`, the locale from `i18nLocale$` and users from `orgUsers$`.
   */
  formatFieldValueForComponentOrReference,
  /**
   * @deprecated Call `fieldUtils.getFieldValueForComponentOrReference` (`@ardoq/scope-data`), or `fieldOperations.getFieldValue` (`@ardoq/core`).
   */
  getFieldValueForComponentOrReference,
  /**
   * @deprecated Read `fields$.byName` and narrow with the `fieldOperations` predicates -- `isDateOnlyField`, `isDateTimeField`, `isNumberField`, `isListField`, `isURLField` (`@ardoq/core`).
   */
  getFieldTypeFromFieldName,
};
